import { computeGameweekDetail } from '../season'
import { chipDefinitions } from '../mockChips'
import { getQuestsForGameweek } from '../quests'
import type { QuestResult } from '../questResults'
import { buildQuestResult } from './gameweekGrid/questCardProps'
import QuestCard from './QuestCard'
import OtherPlayerCallsGrid, { type FixtureState } from './OtherPlayerCallsGrid'
import type { Game, Prediction, QuestId, QuestPickState } from '../types'
import './OtherPlayerGameweekReveal.css'

export type { FixtureState }

interface OtherPlayerGameweekRevealProps {
  gameweek: number
  games: Game[]
  predictions: Record<string, Prediction>
  bankerGameId: string | null
  activeChipId: string | null
  fixtureStates: Record<string, FixtureState>
  questPicks: Partial<Record<QuestId, QuestPickState>>
  /** The gameweek's graded quest truth — app-wide, so this is the same map the
   *  viewer's own reveal reads. An ungraded quest renders no card here: this screen has no
   *  sealed state of its own, and a verdict that has not been computed must not be invented. */
  questResults: Partial<Record<QuestId, QuestResult>>
  /** Which quests this deployment runs. Empty means this section shows
   *  the player's fixtures alone, with no quest cards under them. */
  questsEnabled: QuestId[]
}

/** Read-only "GW {N} Calls" section for another player's profile: the label, what the week is
 * worth so far, the chip they played, their fixture cards and their quest cards. The cards
 * themselves are `OtherPlayerCallsGrid`, shared with the Predict page's top-scorer modal. */
function OtherPlayerGameweekReveal({
  gameweek,
  games,
  predictions,
  bankerGameId,
  activeChipId,
  fixtureStates,
  questPicks,
  questResults,
  questsEnabled,
}: OtherPlayerGameweekRevealProps) {
  const chip = activeChipId
    ? chipDefinitions.find((c) => c.id === activeChipId)
    : undefined
  const quests = games.length > 0 ? getQuestsForGameweek(gameweek, questsEnabled) : []

  // Only fixtures with a final score are in the map, so the header climbs through the weekend
  // instead of jumping at full-time. The per-card figures can't just be summed: the Banker and
  // the chips change the arithmetic, so the total comes from the same function every other
  // total on the app comes from, and agrees with the results modal.
  const results: Record<string, { homeScore: number; awayScore: number }> = {}
  for (const game of games) {
    const fixture = fixtureStates[game.id]
    if (fixture?.kickedOff && fixture.score) results[game.id] = fixture.score
  }
  const hasResults = Object.keys(results).length > 0
  // A zero before anything has kicked off reads as a verdict rather than an empty week.
  const totalPoints = hasResults
    ? computeGameweekDetail(
        gameweek,
        games,
        predictions,
        results,
        bankerGameId,
        activeChipId === 'doubleUp',
        activeChipId === 'cleanSweep',
        activeChipId === 'goalfest'
      ).totalPoints
    : null

  return (
    <section className="other-reveal">
      <p className="profile__section-label label-plain other-reveal__label">
        <span>GW {gameweek} Calls</span>
        {totalPoints !== null && (
          <span className="other-reveal__label-total">{totalPoints} pts</span>
        )}
      </p>

      {chip && <p className="other-reveal__chip">Played {chip.name}</p>}

      <OtherPlayerCallsGrid
        games={games}
        predictions={predictions}
        bankerGameId={bankerGameId}
        fixtureStates={fixtureStates}
      />

      {quests.length > 0 && (
        <div className="other-reveal__quests">
          {quests.map((quest) => {
            const questResult = questResults[quest.id]
            if (!questResult) return null
            const { pick, result } = buildQuestResult(
              quest,
              games,
              questPicks[quest.id],
              questResult
            )
            return (
              <QuestCard
                key={quest.id}
                phase="result"
                question={quest.question}
                pick={pick}
                result={result}
              />
            )
          })}
        </div>
      )}
    </section>
  )
}

export default OtherPlayerGameweekReveal
